import { useContext, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { AuthContext } from '../auth/AuthContext';
import type { DataTableQuery, SortOrder } from '../components/DataTable';

/** Tamanhos de página das listagens. A primeira abertura usa 20. */
export const GRID_PAGE_SIZES = [10, 20, 50, 100] as const;
export const GRID_PAGE_SIZE = 20;

const PREFIX = 'dtsadmin:grid:';

export interface PersistedGridState<F> {
  query: DataTableQuery;
  filters: F;
  setQuery: Dispatch<SetStateAction<DataTableQuery>>;
  setFilters: Dispatch<SetStateAction<F>>;
  /** Atualiza filtros e volta para a primeira página. */
  patchFilters: (partial: Partial<F>) => void;
  clear: () => void;
}

function isPageSize(value: number): boolean {
  return (GRID_PAGE_SIZES as readonly number[]).includes(value);
}

function sanitizeQuery(raw: unknown, fallback: DataTableQuery): DataTableQuery {
  if (!raw || typeof raw !== 'object') return fallback;
  const query = raw as Partial<DataTableQuery>;
  const sortOrder: SortOrder | undefined =
    query.sortOrder === 'asc' || query.sortOrder === 'desc' ? query.sortOrder : fallback.sortOrder;
  return {
    page: typeof query.page === 'number' && query.page > 0 ? query.page : 1,
    pageSize:
      typeof query.pageSize === 'number' && isPageSize(query.pageSize)
        ? query.pageSize
        : fallback.pageSize,
    search: typeof query.search === 'string' ? query.search : fallback.search,
    sortBy: typeof query.sortBy === 'string' ? query.sortBy : fallback.sortBy,
    sortOrder,
    includeInactive:
      typeof query.includeInactive === 'boolean' ? query.includeInactive : fallback.includeInactive,
  };
}

function readSaved<F>(
  key: string,
  userId: number,
  defaultQuery: DataTableQuery,
  defaultFilters: F,
): { query: DataTableQuery; filters: F } | null {
  try {
    const raw = localStorage.getItem(`${PREFIX}${userId}:${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { query?: unknown; filters?: Partial<F> };
    return {
      query: sanitizeQuery(parsed.query, defaultQuery),
      filters: { ...defaultFilters, ...(parsed.filters ?? {}) },
    };
  } catch {
    return null;
  }
}

/**
 * Memória da listagem por usuário, no navegador. A primeira visita usa
 * `defaultQuery` (20 linhas). Filtros, busca, ordenação e quantidade voltam
 * na próxima abertura. `clear` zera tudo para o padrão.
 */
export function usePersistedGridState<F>(
  key: string,
  defaultQuery: DataTableQuery,
  defaultFilters: F,
): PersistedGridState<F> {
  const auth = useContext(AuthContext);
  const userId = auth?.status === 'bootstrapping' ? undefined : (auth?.user?.externalId ?? null);
  const [query, setQuery] = useState(defaultQuery);
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFor, setAppliedFor] = useState<string | undefined>(undefined);
  const applyToken = userId === undefined ? undefined : `${userId}:${key}`;

  if (applyToken !== undefined && appliedFor !== applyToken) {
    const saved = userId != null ? readSaved(key, userId, defaultQuery, defaultFilters) : null;
    setAppliedFor(applyToken);
    setQuery(saved?.query ?? defaultQuery);
    setFilters(saved?.filters ?? defaultFilters);
  }

  useEffect(() => {
    if (userId == null || appliedFor !== `${userId}:${key}`) return;
    try {
      localStorage.setItem(`${PREFIX}${userId}:${key}`, JSON.stringify({ query, filters }));
    } catch {
      // Quota ou modo privado: a tela segue sem persistir.
    }
  }, [key, userId, appliedFor, query, filters]);

  function patchFilters(partial: Partial<F>): void {
    setFilters((prev) => ({ ...prev, ...partial }));
    setQuery((prev) => ({ ...prev, page: 1 }));
  }

  function clear(): void {
    setQuery(defaultQuery);
    setFilters(defaultFilters);
  }

  return { query, filters, setQuery, setFilters, patchFilters, clear };
}
